import { useState, useEffect } from "react";
import {
  TrendingUp,
  Flame,
  Search,
  DollarSign,
  ExternalLink,
  ArrowRight,
  Loader2,
  RefreshCw,
  BarChart3,
  Globe,
  Clock,
  Zap,
  Star,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import WatchlistButton from "../components/WatchlistButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAG_MAP = {
  "France": "\u{1F1EB}\u{1F1F7}",
  "USA": "\u{1F1FA}\u{1F1F8}",
  "Allemagne": "\u{1F1E9}\u{1F1EA}",
  "UK": "\u{1F1EC}\u{1F1E7}",
  "Chine": "\u{1F1E8}\u{1F1F3}",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyDiscovery, setDailyDiscovery] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, feedRes, discoveryRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/dashboard/feed`),
        axios.get(`${API}/daily-discovery`).catch(() => ({ data: { discovery: null } })),
      ]);
      setStats(statsRes.data);
      setFeed(feedRes.data);
      setDailyDiscovery(discoveryRes.data?.discovery);
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#00E676]" size={48} />
      </div>
    );
  }

  const deals = feed?.high_margin_deals || [];
  const comparisons = feed?.recent_comparisons || [];
  const scans = feed?.recent_scans || [];
  const searchTerms = feed?.recent_search_terms || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">DASHBOARD</h1>
          <p className="text-muted-foreground mt-1 uppercase tracking-wider text-xs sm:text-sm">
            Vue d'ensemble temps réel
          </p>
        </div>
        <button
          onClick={fetchData}
          className="neo-button-secondary flex items-center gap-2"
          data-testid="refresh-dashboard"
        >
          <RefreshCw size={18} /> Actualiser
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-cards">
          <div className="neo-card p-4 sm:p-5">
            <TrendingUp className="text-[#00E676] mb-2" size={22} />
            <p className="text-2xl sm:text-3xl font-extrabold">{stats.total_opportunities}</p>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Opportunités
            </p>
          </div>
          <div className="neo-card p-4 sm:p-5">
            <Flame className="text-[#FF5722] mb-2" size={22} />
            <p className="text-2xl sm:text-3xl font-extrabold">{stats.high_margin_alerts}</p>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Haute marge
            </p>
          </div>
          <div className="neo-card p-4 sm:p-5">
            <Search className="text-[#00E676] mb-2" size={22} />
            <p className="text-2xl sm:text-3xl font-extrabold">{stats.average_margin}%</p>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Marge moyenne
            </p>
          </div>
          <div className="neo-card p-4 sm:p-5">
            <DollarSign className="text-[#00E676] mb-2" size={22} />
            <p className="text-2xl sm:text-3xl font-extrabold">{stats.total_potential_profit.toFixed(0)}€</p>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-bold mt-1">
              Profit potentiel
            </p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => navigate("/scanner")}
          className="neo-card p-4 text-left hover:border-[#00E676] transition-colors group"
          data-testid="quick-scanner"
        >
          <Search size={20} className="text-[#00E676] mb-2 group-hover:scale-110 transition-transform" />
          <p className="font-bold text-sm">Scanner</p>
          <p className="text-xs text-muted-foreground">Scanner un produit par mots-clés ou image</p>
        </button>
        <button
          onClick={() => navigate("/comparateur")}
          className="neo-card p-4 text-left hover:border-[#00E676] transition-colors group"
          data-testid="quick-comparateur"
        >
          <BarChart3 size={20} className="text-[#00E676] mb-2 group-hover:scale-110 transition-transform" />
          <p className="font-bold text-sm">Comparateur</p>
          <p className="text-xs text-muted-foreground">Comparer les prix sur 5 marchés</p>
        </button>
        <button
          onClick={() => navigate("/opportunities")}
          className="neo-card p-4 text-left hover:border-[#00E676] transition-colors group"
          data-testid="quick-opportunities"
        >
          <Flame size={20} className="text-[#00E676] mb-2 group-hover:scale-110 transition-transform" />
          <p className="font-bold text-sm">Opportunités</p>
          <p className="text-xs text-muted-foreground">Voir toutes les opportunités d'arbitrage</p>
        </button>
      </div>

      {/* Daily Discovery */}
      {dailyDiscovery && (
        <div className="neo-card p-4 sm:p-6 border-[#FFCC00] bg-[#FFCC00]/5" data-testid="daily-discovery">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={20} className="text-[#FFCC00]" />
            <h2 className="text-base md:text-lg font-bold uppercase tracking-tight">Trouvaille du jour</h2>
            {dailyDiscovery.discovered_at && (
              <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(dailyDiscovery.discovered_at)}</span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            {dailyDiscovery.product_image && (
              <img src={dailyDiscovery.product_image} alt="" className="w-20 h-20 object-contain border-2 border-[#0A0A0A] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-bold text-sm sm:text-base truncate">{dailyDiscovery.product_name}</p>
                <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase border flex-shrink-0 ${dailyDiscovery.condition === "NEW" ? "border-[#00E676]/50 text-[#00E676]" : "border-[#FF6B35]/50 text-[#FF6B35]"}`}>
                  {dailyDiscovery.condition === "NEW" ? "Neuf" : "Occasion"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground font-bold">Acheter</p>
                  <p className="text-xs text-muted-foreground">{dailyDiscovery.buy_platform}</p>
                  <p className="font-bold">{dailyDiscovery.buy_price?.toFixed(2)} EUR</p>
                </div>
                <div className="flex items-center justify-center"><ArrowRight className="text-[#FFCC00]" size={20} /></div>
                <div>
                  <p className="text-[10px] uppercase text-muted-foreground font-bold">Vendre</p>
                  <p className="text-xs text-muted-foreground">{dailyDiscovery.sell_platform}</p>
                  <p className="font-bold text-[#00E676]">{dailyDiscovery.sell_price?.toFixed(2)} EUR</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              <div className="bg-[#FFCC00] text-[#0A0A0A] px-3 py-2 font-extrabold text-lg border-2 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A] transform rotate-2">
                +{dailyDiscovery.margin_percent?.toFixed(1)}%
              </div>
              <span className="text-xs font-bold text-[#00E676]">+{dailyDiscovery.margin?.toFixed(2)} EUR</span>
              {dailyDiscovery.buy_url && (
                <a href={dailyDiscovery.buy_url} target="_blank" rel="noopener noreferrer" className="neo-button-secondary text-xs px-3 py-1 flex items-center gap-1" data-testid="daily-discovery-link">
                  <ExternalLink size={12} /> Voir
                </a>
              )}
              <WatchlistButton product={{
                product_name: dailyDiscovery.product_name,
                search_query: dailyDiscovery.product_name,
                product_image: dailyDiscovery.product_image,
                buy_platform: dailyDiscovery.buy_platform,
                buy_price: dailyDiscovery.buy_price,
                sell_platform: dailyDiscovery.sell_platform,
                sell_price: dailyDiscovery.sell_price,
                margin: dailyDiscovery.margin,
                margin_percent: dailyDiscovery.margin_percent,
                buy_url: dailyDiscovery.buy_url,
                sell_url: dailyDiscovery.sell_url,
              }} className="text-xs px-3 py-1" />
            </div>
          </div>
        </div>
      )}

      {/* High Margin Deals */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm sm:text-base font-bold uppercase tracking-tight flex items-center gap-2">
            <Flame className="text-[#FF5722]" size={20} />
            Grosses marges détectées
          </h2>
          <button
            onClick={() => navigate("/opportunities")}
            className="neo-button-secondary text-xs"
            data-testid="see-all-opps"
          >
            Voir tout
          </button>
        </div>

        {deals.length > 0 ? (
          <div className="space-y-3" data-testid="high-margin-deals">
            {deals.map((opp, idx) => (
              <div
                key={opp.id || idx}
                className="neo-card p-4 relative"
                data-testid={`deal-${idx}`}
              >
                <div className="absolute top-3 right-3 bg-[#00E676] text-[#0A0A0A] border-2 border-[#0A0A0A] px-2.5 py-0.5 font-bold text-sm shadow-[2px_2px_0px_#0A0A0A]">
                  +{opp.margin_percent}%
                </div>
                <div className="flex gap-3 items-start pr-20">
                  {opp.product_image && (
                    <img src={opp.product_image} alt="" className="w-12 h-12 object-contain border border-[#0A0A0A]/20 flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm truncate">{opp.product_name}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs sm:text-sm flex-wrap">
                      <span>
                        <span className="text-muted-foreground">Achat:</span>{" "}
                        <strong>{opp.buy_platform}</strong> {opp.buy_price?.toFixed(2)}€
                      </span>
                      <ArrowRight size={12} className="text-[#00E676] hidden sm:block" />
                      <span>
                        <span className="text-muted-foreground">Vente:</span>{" "}
                        <strong className="text-[#00E676]">{opp.sell_platform}</strong> {opp.sell_price?.toFixed(2)}€
                      </span>
                      <span className="font-bold text-[#00E676]">+{opp.margin?.toFixed(2)}€</span>
                      <WatchlistButton product={opp} />
                    </div>
                    {opp.scanned_at && (
                      <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                        <Clock size={9} /> {timeAgo(opp.scanned_at)} · {opp.source_query}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="neo-card p-8 text-center" data-testid="no-deals">
            <Flame className="mx-auto mb-3 text-muted-foreground" size={40} />
            <p className="font-bold text-muted-foreground">Aucune grosse marge en cache</p>
            <p className="text-sm text-muted-foreground mt-1">
              Lancez un scan ou une recherche pour détecter des opportunités
            </p>
            <button
              onClick={() => navigate("/scanner")}
              className="neo-button-primary mt-4 inline-flex items-center gap-2 text-sm"
            >
              <Search size={16} /> Scanner un produit
            </button>
          </div>
        )}
      </div>

      {/* Recent Comparisons */}
      {comparisons.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm sm:text-base font-bold uppercase tracking-tight flex items-center gap-2">
              <Globe className="text-[#00BCD4]" size={20} />
              Comparaisons récentes
            </h2>
            <button
              onClick={() => navigate("/comparateur")}
              className="neo-button-secondary text-xs"
            >
              Comparer
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-comparisons">
            {comparisons.map((comp, idx) => {
              const validMarkets = comp.results?.filter(r => r.best_price) || [];
              const best = validMarkets.length ? validMarkets.reduce((a, b) => a.best_price < b.best_price ? a : b) : null;
              return (
                <div
                  key={idx}
                  className="neo-card p-4 hover:border-[#00E676] transition-colors cursor-pointer"
                  onClick={() => navigate("/comparateur")}
                  data-testid={`comparison-${idx}`}
                >
                  <p className="font-bold text-sm truncate">{comp.query}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {validMarkets.slice(0, 5).map((m, mi) => (
                      <span key={mi} className="text-lg" title={`${m.market}: ${m.best_price}`}>
                        {FLAG_MAP[{"fr":"France","us":"USA","de":"Allemagne","gb":"UK","cn":"Chine"}[m.market] || ""] || m.market}
                      </span>
                    ))}
                  </div>
                  {best && (
                    <p className="text-xs mt-2">
                      Meilleur prix :{" "}
                      <span className="text-[#00E676] font-bold">
                        {best.best_price?.toFixed(2)} {best.currency}
                      </span>
                      <span className="text-muted-foreground"> ({best.market?.toUpperCase()})</span>
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock size={9} /> {timeAgo(comp.compared_at)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Scans */}
      {scans.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm sm:text-base font-bold uppercase tracking-tight flex items-center gap-2">
              <Search className="text-[#FFCC00]" size={20} />
              Scans récents
            </h2>
            <button
              onClick={() => navigate("/scanner")}
              className="neo-button-secondary text-xs"
            >
              Scanner
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-scans">
            {scans.map((scan, idx) => (
              <div
                key={idx}
                className="neo-card p-4 hover:border-[#00E676] transition-colors cursor-pointer"
                onClick={() => navigate("/scanner")}
                data-testid={`scan-${idx}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-bold text-sm truncate flex-1">{scan.query || scan.scan_type}</p>
                  <span className="text-lg ml-2">
                    {{"fr":"\u{1F1EB}\u{1F1F7}","us":"\u{1F1FA}\u{1F1F8}","de":"\u{1F1E9}\u{1F1EA}","gb":"\u{1F1EC}\u{1F1E7}","cn":"\u{1F1E8}\u{1F1F3}"}[scan.country] || "\u{1F30D}"}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground">{scan.products_count || 0} produits</span>
                  {scan.opportunities_count > 0 && (
                    <span className="text-[#00E676] font-bold">{scan.opportunities_count} opportunités</span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                  <Clock size={9} /> {timeAgo(scan.scanned_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Search Terms */}
      {searchTerms.length > 0 && (
        <div>
          <h2 className="text-sm font-bold uppercase tracking-tight flex items-center gap-2 mb-3">
            <Zap className="text-[#FFCC00]" size={18} />
            Recherches récentes
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="recent-searches">
            {searchTerms.map((term, idx) => (
              <button
                key={idx}
                onClick={() => navigate("/scanner")}
                className="neo-button-secondary text-xs px-3 py-1.5"
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Platform Analysis */}
      {stats?.top_platforms && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="neo-card p-5">
            <h2 className="text-sm font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
              <Star size={16} className="text-[#00E676]" />
              Top plateformes (achat)
            </h2>
            <div className="space-y-2">
              {stats.top_platforms.buy.map((p, i) => (
                <a
                  key={i}
                  href={p.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex justify-between items-center p-2.5 border-b border-[#0A0A0A]/10 dark:border-white/10 hover:bg-[#00E676]/10 transition-colors"
                  data-testid={`buy-platform-${i}`}
                >
                  <span className="font-semibold text-sm flex items-center gap-2">
                    {p.name}
                    <ExternalLink size={10} className="text-muted-foreground" />
                  </span>
                  <span className="text-[#00E676] font-bold text-sm">{p.count} offres</span>
                </a>
              ))}
            </div>
          </div>
          <div className="neo-card p-5">
            <h2 className="text-sm font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
              <Star size={16} className="text-[#FFCC00]" />
              Top plateformes (vente)
            </h2>
            <div className="space-y-2">
              {stats.top_platforms.sell.map((p, i) => (
                <a
                  key={i}
                  href={p.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex justify-between items-center p-2.5 border-b border-[#0A0A0A]/10 dark:border-white/10 hover:bg-[#00E676]/10 transition-colors"
                  data-testid={`sell-platform-${i}`}
                >
                  <span className="font-semibold text-sm flex items-center gap-2">
                    {p.name}
                    <ExternalLink size={10} className="text-muted-foreground" />
                  </span>
                  <span className="text-[#00E676] font-bold text-sm">{p.count} offres</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
